import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { submitCaseToEngine } from '../services/engineService';
import { UPLOAD_CATEGORIES, DEFAULT_ACCEPT, filterCbctFiles } from '../config/uploadCategories';
import { TOOTH_LIBRARIES } from '../config/toothLibraries';

/**
 * ARCHX AI — Edit Case (add / modify scans on an already-submitted case)
 * --------------------------------------------------------------------------
 * Lets a user reopen a submitted case and:
 *   • add new files to any category (uploaded to the same Storage path)
 *   • remove files that were uploaded by mistake
 *   • optionally re-run the engine with the updated file set
 * Appends to the case's files[] + fileCount in Firestore — no delete-and-restart.
 */
export default function EditCase({ user, setIsUploading }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [loadErr, setLoadErr]   = useState(null);
  const [existing, setExisting] = useState([]);   // files already on the case
  const [removed, setRemoved]   = useState([]);    // file objects marked for deletion
  const [pending, setPending]   = useState({});    // { [catKey]: File[] } new files staged
  const [rerun, setRerun]       = useState(true);
  const [toothLib, setToothLib] = useState('');
  const [busy, setBusy]         = useState(false);
  const [progress, setProgress] = useState(0);
  const inputs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'cases', id));
        if (!snap.exists()) { setLoadErr('Case not found.'); return; }
        const data = { id: snap.id, ...snap.data() };
        if (data.practiceId !== user.uid) { setLoadErr('You don’t have access to this case.'); return; }
        setCaseData(data);
        setExisting(Array.isArray(data.files) ? data.files : []);
        setToothLib(data.toothLibrary || '');
      } catch (e) {
        setLoadErr(e.message);
      }
    })();
  }, [id, user]);

  const filesInCat = (key) => existing.filter(f => f.category === key && !removed.includes(f));
  const pendingInCat = (key) => pending[key] || [];

  const pickFiles = (key, fileList) => {
    let files = Array.from(fileList);
    if (key === 'cbct') files = filterCbctFiles(files);
    if (!files.length) return;
    setPending(prev => ({ ...prev, [key]: [...(prev[key] || []), ...files] }));
  };

  const unstage = (key, idx) => {
    setPending(prev => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));
  };

  const markRemoved = (f) => setRemoved(prev => [...prev, f]);
  const undoRemoved = (f) => setRemoved(prev => prev.filter(x => x !== f));

  const pendingCount = Object.values(pending).reduce((n, arr) => n + arr.length, 0);
  const libChanged = caseData ? (toothLib !== (caseData.toothLibrary || '')) : false;
  const hasChanges = pendingCount > 0 || removed.length > 0 || libChanged;

  const handleSave = async () => {
    if (!hasChanges || busy) return;
    setBusy(true);
    if (setIsUploading) setIsUploading(true);
    try {
      const base = `cases/${caseData.practiceId}/${caseData.caseNumber}`;

      // 1. Delete files marked for removal (ignore already-gone)
      for (const f of removed) {
        if (!f?.path) continue;
        try { await deleteObject(ref(storage, f.path)); }
        catch (e) { if (e.code !== 'storage/object-not-found') throw e; }
      }

      // 2. Upload newly staged files
      const staged = [];
      Object.entries(pending).forEach(([category, arr]) =>
        arr.forEach(file => staged.push({ file, category })));

      const uploaded = [];
      for (let i = 0; i < staged.length; i++) {
        const { file, category } = staged[i];
        const path = `${base}/${category}/${file.name}`;
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(ref(storage, path), file);
          task.on('state_changed',
            snap => {
              const fileProg = snap.bytesTransferred / snap.totalBytes;
              setProgress(Math.round(((i + fileProg) / staged.length) * 100));
            },
            reject,
            () => { uploaded.push({ name: file.name, path, size: file.size, category }); resolve(); }
          );
        });
      }

      // 3. Merge: keep existing-minus-removed, add uploaded (de-dupe by path)
      const keep = existing.filter(f => !removed.includes(f));
      const byPath = new Map();
      [...keep, ...uploaded].forEach(f => byPath.set(f.path, f));
      const newFiles = Array.from(byPath.values());

      // 4. Persist to Firestore (incl. the selected tooth library)
      const caseRef = doc(db, 'cases', caseData.id);
      await updateDoc(caseRef, {
        files: newFiles,
        fileCount: newFiles.length,
        toothLibrary: toothLib || null,
        updatedAt: serverTimestamp(),
      });

      // 5. Optionally re-run the engine with the updated file set + library
      if (rerun) {
        try {
          const resp = await submitCaseToEngine(caseData.caseNumber, caseData.practiceId, newFiles, toothLib);
          await updateDoc(caseRef, {
            engineJobId: resp.job_id,
            engineStatus: 'processing',
            status: 'processing',
          });
        } catch (engineErr) {
          console.warn('Engine re-run failed (will retry):', engineErr.message);
          await updateDoc(caseRef, { engineStatus: 'queued' });
          alert('Files saved, but the engine could not be reached to re-process. It will retry — status set to “queued”.');
        }
      }

      navigate('/dashboard');
    } catch (err) {
      console.error('Edit save error:', err);
      alert('Could not save changes: ' + err.message);
    } finally {
      setBusy(false);
      if (setIsUploading) setIsUploading(false);
    }
  };

  if (loadErr) return <Shell><div style={msg}>{loadErr} <button style={linkBtn} onClick={() => navigate('/dashboard')}>Back to dashboard</button></div></Shell>;
  if (!caseData) return <Shell><div style={msg}>Loading case…</div></Shell>;

  return (
    <Shell>
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
          <div>
            <h2 style={{ margin: 0 }}>Edit case — {caseData.caseNumber}</h2>
            <div style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
              {caseData.patientLast}, {caseData.patientFirst} · {existing.filter(f => !removed.includes(f)).length + pendingCount} file(s)
            </div>
          </div>
          <button style={linkBtn} onClick={() => navigate('/dashboard')}>← Back</button>
        </div>

        <div style={{ padding: '10px 16px', borderRadius: '10px', background: '#fffbf0', border: '1px solid #fde68a', color: '#92400e', fontSize: '13px', margin: '12px 0 22px' }}>
          Add new scans to any box, or remove files uploaded by mistake. Your existing files stay unless you remove them.
        </div>

        {UPLOAD_CATEGORIES.map(cat => {
          const current = filesInCat(cat.key);
          const staged  = pendingInCat(cat.key);
          return (
            <div key={cat.key} className="card" style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600 }}>
                  {cat.label} {cat.required && <span style={{ color: '#c8922a', fontSize: '12px' }}>· required</span>}
                </div>
                <div>
                  <input
                    ref={el => (inputs.current[cat.key] = el)}
                    type="file" multiple
                    accept={cat.accept || DEFAULT_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={e => { pickFiles(cat.key, e.target.files); e.target.value = ''; }}
                  />
                  <button style={addBtn} disabled={busy} onClick={() => inputs.current[cat.key]?.click()}>+ Add files</button>
                </div>
              </div>
              {cat.key === 'cbct' && (
                <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>{cat.hint}</div>
              )}

              {(current.length > 0 || staged.length > 0) && (
                <div style={{ marginTop: '12px', display: 'grid', gap: '6px' }}>
                  {current.map((f, i) => (
                    <FileRow key={`e-${i}`} name={f.name} size={f.size}
                      onRemove={() => markRemoved(f)} removeLabel="Remove" />
                  ))}
                  {staged.map((f, i) => (
                    <FileRow key={`p-${i}`} name={f.name} size={f.size} isNew
                      onRemove={() => unstage(cat.key, i)} removeLabel="Undo" />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Removed-but-not-saved list */}
        {removed.length > 0 && (
          <div className="card" style={{ marginBottom: '14px', borderColor: '#fecaca' }}>
            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '8px' }}>Will be deleted on save</div>
            {removed.map((f, i) => (
              <div key={i} style={{ fontSize: '13px', color: '#991b1b', display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ textDecoration: 'line-through' }}>{f.name}</span>
                <button style={linkBtn} onClick={() => undoRemoved(f)}>Undo</button>
              </div>
            ))}
          </div>
        )}

        {busy && (
          <div style={{ margin: '12px 0' }}>
            <div style={{ height: '8px', background: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#c8922a', transition: 'width .2s' }} />
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>Saving… {progress}%</div>
          </div>
        )}

        {/* Design settings — change the tooth library and re-run for a redesign */}
        <div className="card" style={{ margin: '18px 0 0' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>Design settings</div>
          <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>
            Tooth library (design){libChanged && <span style={{ color: '#c8922a', fontWeight: 700 }}> · changed</span>}
          </label>
          <select className="form-select" value={toothLib} onChange={e => setToothLib(e.target.value)}
            style={{ maxWidth: '420px' }}>
            <option value="">Engine default</option>
            {TOOTH_LIBRARIES.map(l => (
              <option key={l.slug} value={l.slug}>{l.name}</option>
            ))}
          </select>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#444', margin: '18px 0' }}>
          <input type="checkbox" checked={rerun} onChange={e => setRerun(e.target.checked)} />
          Re-run the engine after saving (recommended when you add scans or change the library)
        </label>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-gold" disabled={!hasChanges || busy} onClick={handleSave}
            style={{ opacity: (!hasChanges || busy) ? 0.5 : 1 }}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button style={linkBtn} disabled={busy} onClick={() => navigate('/dashboard')}>Cancel</button>
        </div>
      </div>
    </Shell>
  );
}

function FileRow({ name, size, onRemove, removeLabel, isNew }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: isNew ? '#f0fdf4' : '#f8f8f8', borderRadius: '8px', fontSize: '13px' }}>
      <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {isNew && <span style={{ color: '#16a34a', fontWeight: 700, marginRight: '6px' }}>NEW</span>}
        {name} {size ? <span style={{ color: '#aaa' }}>· {(size / 1024 / 1024).toFixed(1)} MB</span> : null}
      </span>
      <button style={linkBtn} onClick={onRemove}>{removeLabel}</button>
    </div>
  );
}

function Shell({ children }) {
  return (
    <>
      <nav className="navbar">
        <div className="nav-logo"><img src="/logo.png" alt="ARCHX AI" style={{ height: '100px', width: 'auto' }} /></div>
      </nav>
      {children}
    </>
  );
}

const msg = { padding: '60px', textAlign: 'center', color: '#666' };
const linkBtn = { border: 'none', background: 'transparent', color: '#c8922a', fontWeight: 600, fontSize: '13px', cursor: 'pointer' };
const addBtn = { padding: '7px 14px', borderRadius: '8px', border: '1px solid #c8922a', background: 'white', color: '#c8922a', fontWeight: 600, fontSize: '13px', cursor: 'pointer' };
